import React from "react";
import PropTypes from "prop-types";

import useSheetStack from "../common/useSheetStack";
import SheetStack from "../common/SheetStack";
import { ADD_V2_EDIT } from "./events";
import AddictionForm from "./AddictionForm";

/**
 * Addictions editor rail — a stack of update sheets, newest on top.
 *
 * Board 3071:2446 draws the update dialog as a bordered card with a
 * chevron-up collapse and no scrim, which is the rail idiom the other v3
 * sections already use; it was previously a centred modal over a backdrop.
 *
 * This rail passes NO addEvents, and that is deliberate rather than an
 * oversight: the addiction list is fixed at four server-seeded rows with no
 * add-new or delete endpoint, so the only sheet that can ever open is an
 * update of an existing record. Rule 1 of the stack (never bury a blank add
 * sheet) is therefore unreachable here; Rule 2 (one sheet per record) does
 * the real work — clicking Smoking twice surfaces the open sheet instead of
 * cloning it, which would otherwise let two writes race the same version.
 */

const EDIT = [ADD_V2_EDIT];

const getRecord = (payload) => payload && payload.addiction;
const getRecordId = (r) => r && r.key;
const describeRecord = (r) => (r && r.label) || "addiction";
const dupMessage = (r) => `${describeRecord(r)} is already open for update.`;

const AddictionEditor = ({ patient }) => {
  const patientId = patient && (patient.id || patient.patientID);

  const stack = useSheetStack({
    editEvents: EDIT,
    getRecord,
    getRecordId,
    dupMessage,
    enabled: !!patientId,
  });

  if (!patientId) {
    return <div className="ph-slot-empty is-editor"><p>Waiting for patient…</p></div>;
  }

  return (
    <SheetStack
      stack={stack}
      className="ad3-editor"
      describe={(s) => `Update ${describeRecord(s.editing)}`}
    >
      {(sheet, props) => <AddictionForm patientId={patientId} {...props} />}
    </SheetStack>
  );
};

AddictionEditor.propTypes = {
  patient: PropTypes.object,
};

export default AddictionEditor;
